import React from "react";
import { ArrowRight } from "lucide-react";

/**
 * ServiceHeroVisual — a service-specific 3-node SVG illustration
 * placed right after the hero on every service page.
 * Pattern: Inputs → JFT Engine → Outputs.
 *
 * The visual content adapts per service slug.
 */
const PRESETS = {
  "ai-automation": {
    de: { in: ["Leads", "Tickets", "Daten"], engine: "AI-Agent", out: ["Qualifizierte\nPipeline", "Automatische\nAntworten", "Live-\nDashboards"] },
    en: { in: ["Leads", "Tickets", "Data"], engine: "AI Agent", out: ["Qualified\npipeline", "Automated\nresponses", "Live\ndashboards"] },
  },
  "web-development": {
    de: { in: ["Mockup", "Inhalte", "Brand"], engine: "Build", out: ["Performante\nWebsite", "CMS für\nMarketing", "SEO-fertig"] },
    en: { in: ["Mockup", "Content", "Brand"], engine: "Build", out: ["High-perf\nwebsite", "CMS for\nmarketing", "SEO-ready"] },
  },
  "digital-marketing": {
    de: { in: ["Audit", "Personas", "KPIs"], engine: "Growth-\nEngine", out: ["Organischer\nTraffic", "Bessere\nFunnel", "Klares\nReporting"] },
    en: { in: ["Audit", "Personas", "KPIs"], engine: "Growth\nengine", out: ["Organic\ntraffic", "Better\nfunnels", "Clear\nreporting"] },
  },
  "content-branding": {
    de: { in: ["Themen", "Persona", "Kanäle"], engine: "Content-\nMotor", out: ["Editorial-\nKalender", "Social-\nAssets", "Sales-\nDecks"] },
    en: { in: ["Topics", "Persona", "Channels"], engine: "Content\nengine", out: ["Editorial\ncalendar", "Social\nassets", "Sales\ndecks"] },
  },
  "brand-design": {
    de: { in: ["Werte", "Markt", "Stil"], engine: "Design-\nSystem", out: ["Logo &\nIdentity", "Designsystem\n(Figma)", "Brand-\nGuidelines"] },
    en: { in: ["Values", "Market", "Style"], engine: "Design\nsystem", out: ["Logo &\nidentity", "Design system\n(Figma)", "Brand\nguidelines"] },
  },
  "strategy-consulting": {
    de: { in: ["Audit", "Markt", "Ziele"], engine: "Roadmap", out: ["12-Monats-\nPlan", "Benannte\nOwner", "Quartals-\nReviews"] },
    en: { in: ["Audit", "Market", "Goals"], engine: "Roadmap", out: ["12-month\nplan", "Named\nowners", "Quarterly\nreviews"] },
  },
};

const Tag = ({ text }) => (
  <div className="glass rounded-xl px-3 py-2 text-xs font-display font-medium text-white whitespace-pre-line text-center leading-tight min-w-[88px]">
    {text}
  </div>
);

export const ServiceHeroVisual = ({ slug, locale, sectionLabels }) => {
  const data = PRESETS[slug]?.[locale] || PRESETS["ai-automation"][locale] || PRESETS["ai-automation"].en;
  const isDE = locale === "de";
  return (
    <section className="px-6 md:px-12 lg:px-24 pb-10" aria-labelledby="svc-flow-h">
      <div className="max-w-6xl mx-auto glass-strong rounded-3xl p-8 md:p-10 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[460px] h-[460px] rounded-full bg-brand-orange/12 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#7AB0FF]/10 blur-3xl pointer-events-none" />

        <div className="relative flex items-center gap-3 mb-6">
          <span className="w-12 h-px bg-brand-orange" />
          <span className="label-mono" id="svc-flow-h">{sectionLabels?.kicker || (isDE ? "So funktioniert es" : "How it works")}</span>
        </div>

        <div className="relative grid md:grid-cols-[1fr_auto_1.2fr_auto_1.4fr] items-center gap-5 md:gap-3">
          {/* Inputs */}
          <div className="space-y-2.5">
            <div className="label-mono text-white/55 mb-2">{isDE ? "Inputs" : "Inputs"}</div>
            {data.in.map((t, i) => (
              <Tag key={i} text={t} />
            ))}
          </div>

          <ArrowRight className="hidden md:block w-6 h-6 text-brand-orange justify-self-center" />

          {/* Engine */}
          <div className="flex flex-col items-center justify-center my-4 md:my-0">
            <div className="relative">
              <div className="absolute inset-0 rounded-2xl bg-brand-orange/20 blur-xl animate-pulse" style={{ animationDuration: "2.8s" }} />
              <div className="relative glass-strong border-brand-orange/40 rounded-2xl px-6 py-7 text-center min-w-[160px] shadow-[0_18px_48px_rgba(255,106,0,0.25)]">
                <div className="label-mono text-brand-orange mb-2">JFT</div>
                <div className="font-display font-bold text-white whitespace-pre-line leading-tight text-lg">{data.engine}</div>
              </div>
            </div>
          </div>

          <ArrowRight className="hidden md:block w-6 h-6 text-brand-orange justify-self-center" />

          {/* Outputs */}
          <div className="space-y-2.5">
            <div className="label-mono text-white/55 mb-2">{isDE ? "Outputs" : "Outputs"}</div>
            <div className="grid grid-cols-1 gap-2.5">
              {data.out.map((t, i) => (
                <div key={i} className="glass rounded-xl px-3 py-2 text-xs font-display font-medium text-white whitespace-pre-line text-center leading-tight border-brand-orange/30">
                  {t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
